<template>
  <section>
    <div>
      <div class="grid lg:grid-cols-2 grid-cols-1">
        <div class="flex items-center px-6 py-12 md:p-20 bg-gray-100">
          <div class="w-full">
            <h2 class="text-gray-900 font-serif text-xl md:text-2xl mb-9">{{ $t('contacts.contact-us') }}</h2>
            <form @submit.prevent="submitForm">
              <div class="mb-8">
                <label class="mb-2 block" for="name">{{ $t('contacts.name-label') }}</label>
                <input v-model="name" v-bind="nameAttrs" id="name" name="name" type="text"
                  :class="{ 'input-error': errors.name }"
                  class="w-full h-14 shadow-sm text-gray-600 placeholder:text-gray-400 text-base font-sans leading-7 rounded-lg border border-gray-200 focus:outline-none py-2 px-4"
                  :placeholder="$t('contacts.name-placeholder')" />
              </div>

              <div class="mb-8">
                <label class="mb-2 block" for="email">{{ $t('contacts.email-label') }}</label>
                <input v-model="email" v-bind="emailAttrs" id="email" name="email" type="email"
                  :class="{ 'input-error': errors.email }"
                  class="w-full h-14 shadow-sm text-gray-600 placeholder:text-gray-400 text-base font-sans leading-7 rounded-lg border border-gray-200 focus:outline-none py-2 px-4"
                  :placeholder="$t('contacts.email-placeholder')" />
              </div>

              <div class="mb-8">
                <label class="mb-2 block" for="message">{{ $t('contacts.message-placeholder') }}</label>
                <textarea v-model="message" v-bind="messageAttrs" id="message" name="message"
                  :class="{ 'input-error': errors.message }"
                  class="w-full h-48 shadow-sm resize-none text-gray-600 placeholder:text-gray-400 text-base font-sans leading-7 rounded-2xl border border-gray-200 focus:outline-none px-4 py-4"
                  :placeholder="$t('contacts.message-placeholder')"></textarea>
              </div>

              <div class="mb-8">
                <div class="flex items-center">
                  <input type="checkbox" id="privacy" name="privacy" v-model="acceptedPrivacy" v-bind="acceptedPrivacyAttrs"
                    class="mr-2" />
                  <i18n-t for="checkbox" keypath="contacts.privacy-acceptance" tag="label" scope="global">
                    <template v-slot:url>
                      <NuxtLink class="cursor-pointer underline" :to="localePath('/privacy-policy')" target="_blank">{{
                        $t('contacts.privacy-policy') }}
                      </NuxtLink>
                    </template>
                  </i18n-t>
                </div>
              </div>

              <Button class="w-full bg-tau-red disabled:opacity-50" type="submit" :disabled="!meta.valid || loading">
                {{ $t('contacts.send-button') }}
              </Button>
            </form>
          </div>
        </div>
        <div class="w-full bg-cover bg-opacity-10 bg-bottom bg-[url('/images/contacts.jpg')]">
          <div class="p-10 bg-black w-full h-full bg-opacity-30 flex items-center justify-center ">
            <div class="w-auto h-auto bg-white shadow-xl p-10 md:p-16 rounded-lg">
              <div class="flex items-center justify-center gap-2 mb-10">
                <nuxt-img sizes="xl:100vw lg:100vw md:100vw sm:100vw xs:100vw" class="cursor-pointer h-12"
                  src="/images/logo_nero.png" />
                <span class="text-sm md:text-xl font-serif">{{ $t('contacts.association-name') }}</span>
              </div>
              <a href="https://wa.me/+393496424740" class="flex items-center mb-6">
                <Icon name="mdi:whatsapp" class="mr-2" />
                <h5 class="text-sm md:text-base font-normal leading-6 ml-5">{{ $t('contacts.phone-number') }}</h5>
              </a>
              <a :href="`mailto:associazionetau@virgilio.it`" class="flex items-center mb-6">
                <Icon name="mdi:email-outline" class="mr-2" />
                <h5 class="text-sm md:text-base font-normal leading-6 ml-5">associazionetau@virgilio.it</h5>
              </a>
              <a :href="`https://maps.google.com/maps?q=Via Cimabue 19, Quartu SantElena (CA)`"
                class="flex items-center mb-6">
                <Icon name="mdi:map-marker-outline" class="mr-2 shrink-0" />
                <h5 class="text-sm md:text-base font-normal leading-6 ml-5">Via Cimabue 19, Quartu SantElena (CA)</h5>
              </a>
              <div class="flex items-center justify-center border-t border-gray-100 pt-6">
                <a href="https://www.facebook.com/associazionetau/" class="mr-6">
                  <Icon name="mdi:facebook" class="h-6 w-6" />
                </a>
                <a :href="`https://www.instagram.com/${$t('footer.contact.instagram')}`" class="mr-6">
                  <Icon name="mdi:instagram" class="h-6 w-6" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import * as yup from 'yup';
import { useForm } from 'vee-validate';
import { useReCaptcha } from 'vue-recaptcha-v3';

const loading = ref(false);
const recaptchaInstance = useReCaptcha();

const recaptcha = async () => {
  await recaptchaInstance?.recaptchaLoaded();
  const token = await recaptchaInstance?.executeRecaptcha('submitContactForm');
  return token;
};

const { t } = useI18n();

// Validation schema using yup
const { handleSubmit, errors, reset, defineField, meta } = useForm({
  validationSchema: yup.object({
    name: yup.string().required('Name is required'),
    email: yup.string().email('Invalid email format').required('Email is required'),
    message: yup.string().required('Message is required'),
    acceptedPrivacy: yup.boolean().oneOf([true], 'You must accept the privacy policy').required(),
  }),
});

const [name, nameAttrs] = defineField('name');
const [email, emailAttrs] = defineField('email');
const [message, messageAttrs] = defineField('message');
const [acceptedPrivacy, acceptedPrivacyAttrs] = defineField('acceptedPrivacy');

// Submit function using $fetch
const submitForm = handleSubmit(async (values, actions) => {
  loading.value = true;
  try {
    const recaptchaToken = await recaptcha();
    values.recaptchaToken = recaptchaToken;

    const response = await $fetch('/api/contact', {
      method: 'POST',
      body: values,
    });

    loading.value = false;

    if (response.message === 'Success') {
      actions.resetForm();  // Reset form on successful submission
      alert(t('contacts.success-message'));
    } else {
      alert(t('contacts.error-message'));
    }
  } catch (error) {
    loading.value = false;
    console.error('Form submission failed:', error);
    alert(t('contacts.error-message'));
  }
});
</script>

<style scoped>
.input-error {
  border-color: red;
}

.error-text {
  color: red;
  font-size: 12px;
}
</style>
